import { SearchMethod } from '../../analytics/types';
import {
  ReferralRequestPDFInput,
  ReferralRequestResponse,
  RequestReferralInput,
} from '../../components/app/components/referral-request/types';
import { orderAssistLogger } from '../../components/app/logger';
import {
  Address,
  AppConfig,
  DatasourceConfig,
  EncodedPDFFile,
  FindQuery,
  FindResponse,
  FreeTextQueryInput,
  Geo,
  GetCptsByCodesInput,
  GetPreferredProvidersResponse,
  OrderAssistResultsPDFInput,
  QueryFreeTextResult,
  SendOutcomeInput,
  SupportPersonCapabilityInput,
  WidgetSource,
} from '../../components/app/types';

import {
  buildFindQuery,
  CREATE_PDF,
  CREATE_REFERRAL_REQUEST_PDF,
  GET_APP_CONFIG,
  GET_CPTS_BY_CODES_QUERY,
  GET_DATASOURCE_CONFIG,
  GET_FREE_TEXT_QUERY,
  GET_GEO,
  GET_INSURERS,
  GET_MATCHED_ELIGIBILITY_PLAN,
  GET_PLANS_BY_INSURER,
  PREFERRED_PROVIDERS_QUERY,
  REQUEST_REFERRAL_NEW,
  SEND_OUTCOME_QUERY,
  SUPPORTED_PERSON_CAPABILITY_QUERY,
} from './queries';
import { BaseInternalApi } from '../baseInternalApi';
import { isEmpty } from 'lodash-es';

export class OrderAssistBffApi extends BaseInternalApi {
  constructor(accessToken?: string) {
    super('/api/graphql', accessToken);
  }

  public async find(
    input: FindQuery,
    method: SearchMethod,
    source: WidgetSource | undefined,
  ): Promise<FindResponse> {
    const gqlClient = await this.getClient();

    const shouldIncludeTeladocFields = !isEmpty(input?.tenantSpecificFilters?.garnerSpecialty);
    const findQuery = buildFindQuery(shouldIncludeTeladocFields);

    const result = await gqlClient.query<{ find: FindResponse }, { input: FindQuery }>({
      query: findQuery,
      variables: { input },
      fetchPolicy: 'no-cache',
      context: {
        headers: {
          'x-source': source,
          'x-method': method,
        },
      },
    });
    return result.data?.find;
  }

  public async supportedPersonCapabilities(
    input: SupportPersonCapabilityInput,
    source: WidgetSource | undefined,
  ): Promise<boolean> {
    const gqlClient = await this.getClient();
    const result = await gqlClient.query<
      { supportedPersonCapability: boolean },
      { input: SupportPersonCapabilityInput }
    >({
      query: SUPPORTED_PERSON_CAPABILITY_QUERY,
      variables: { input },
      fetchPolicy: 'no-cache',
      context: {
        headers: {
          'x-source': source,
        },
      },
    });
    return result.data?.supportedPersonCapability;
  }

  public async freeText(
    input: FreeTextQueryInput,
    source?: WidgetSource,
  ): Promise<QueryFreeTextResult> {
    const gqlClient = await this.getClient();
    const result = await gqlClient.query({
      query: GET_FREE_TEXT_QUERY,
      variables: { input },
      context: {
        headers: {
          ...(source && { 'x-source': source }),
        },
      },
    });
    return result.data?.queryFreeTextNew;
  }

  public async sendOutcome(input: SendOutcomeInput): Promise<void> {
    const gqlClient = await this.getClient();
    await gqlClient.mutate({
      mutation: SEND_OUTCOME_QUERY,
      variables: { input },
    });
  }

  public async getPreferredProviders(
    input: FindQuery,
    method: SearchMethod,
    source: WidgetSource | undefined,
  ): Promise<GetPreferredProvidersResponse> {
    const gqlClient = await this.getClient();

    const result = await gqlClient.query<
      { getPreferredProviders: GetPreferredProvidersResponse },
      { input: FindQuery }
    >({
      query: PREFERRED_PROVIDERS_QUERY,
      variables: { input },
      fetchPolicy: 'no-cache',
      context: {
        headers: {
          'x-source': source,
          'x-method': method,
        },
      },
    });
    return result.data?.getPreferredProviders;
  }

  public async getInsurers(source: WidgetSource): Promise<string[]> {
    const gqlClient = await this.getClient();
    const result = await gqlClient.query({
      query: GET_INSURERS,
      fetchPolicy: 'cache-first',
      context: {
        headers: {
          'x-source': source,
        },
      },
    });

    return result.data.getInsurers;
  }

  public async getPlansByInsurer(insurer: string, source: WidgetSource): Promise<string[]> {
    const gqlClient = await this.getClient();
    const result = await gqlClient.query({
      query: GET_PLANS_BY_INSURER,
      variables: { insurer },
      fetchPolicy: 'cache-first',
      context: {
        headers: {
          'x-source': source,
        },
      },
    });

    return result.data.getPlansByInsurer;
  }

  public async getMatchedEligibilityPlan(token: string, source: WidgetSource): Promise<boolean> {
    try {
      const gqlClient = await this.getClient();
      const result = await gqlClient.query({
        query: GET_MATCHED_ELIGIBILITY_PLAN,
        variables: {
          memberToken: undefined,
          sourceVaultToken: token,
        },
        fetchPolicy: 'cache-first',
        context: {
          headers: {
            'x-source': source,
          },
        },
      });
      orderAssistLogger.info('Got getMatchedEligibilityPlan response', {
        token,
        source,
        result,
      });

      return result?.data?.getMatchedEligibilityPlan?.insuranceDetails?.referralRequestRequired;
    } catch (error) {
      orderAssistLogger.warning('getMatchedEligibilityPlan failed', {
        token,
        source,
        error,
      });
      return false;
    }
  }

  public async requestReferral(
    input: RequestReferralInput,
    source: WidgetSource,
  ): Promise<ReferralRequestResponse> {
    const gqlClient = await this.getClient();
    const result = await gqlClient.mutate({
      mutation: REQUEST_REFERRAL_NEW,
      variables: { input },
      context: {
        headers: {
          'x-source': source,
        },
      },
    });
    orderAssistLogger.info('Got requestReferral response', { input, source, result });

    return result?.data?.requestReferral;
  }

  public async getAppConfig(): Promise<AppConfig> {
    const gqlClient = await this.getClient();
    const result = await gqlClient.query({
      query: GET_APP_CONFIG,
      fetchPolicy: 'cache-first',
    });

    return result.data.getAppConfig;
  }

  public async getDatasourceConfig(): Promise<DatasourceConfig> {
    const gqlClient = await this.getClient();
    const result = await gqlClient.query({
      query: GET_DATASOURCE_CONFIG,
      fetchPolicy: 'cache-first',
    });

    return result.data.getDatasourceConfig;
  }

  public async getGeo(
    input: Required<Pick<Geo, 'city' | 'state' | 'addressLine1'>> & Pick<Geo, 'zip'>,
  ): Promise<Address> {
    const gqlClient = await this.getClient();
    const result = await gqlClient.query({
      query: GET_GEO,
      variables: { input },
    });
    orderAssistLogger.info('Got getGeo response', { input, result });

    const { addressLine1, city, state, zip, latitude, longitude } = result.data.getGeo;
    return {
      address1: addressLine1,
      city,
      state,
      zipCode: zip,
      coordinates: { latitude, longitude },
    };
  }

  public async createResultsPdf(
    input: OrderAssistResultsPDFInput,
  ): Promise<EncodedPDFFile | undefined> {
    try {
      const gqlClient = await this.getClient();
      const result = await gqlClient.mutate({
        mutation: CREATE_PDF,
        variables: { input },
      });
      return result.data.createPdf;
    } catch (error) {
      orderAssistLogger.warning('createPDF failed', { input, error });
      return undefined;
    }
  }

  public async createReferralRequestPdf(
    input: ReferralRequestPDFInput,
    source: WidgetSource,
  ): Promise<EncodedPDFFile | undefined> {
    try {
      const gqlClient = await this.getClient();
      const result = await gqlClient.mutate({
        mutation: CREATE_REFERRAL_REQUEST_PDF,
        variables: { input },
        context: {
          headers: {
            'x-source': source,
          },
        },
      });
      return result.data.createReferralRequestPdf;
    } catch (error) {
      orderAssistLogger.warning('createReferralRequestPdf failed', { input, error });
      return undefined;
    }
  }

  public async getCptsByCodes(input: GetCptsByCodesInput): Promise<
    {
      code: string;
      description: string;
    }[]
  > {
    const gqlClient = await this.getClient();
    const result = await gqlClient.query({
      query: GET_CPTS_BY_CODES_QUERY,
      variables: { input },
    });
    return result.data.getCptsByCodes;
  }
}
